import { createMachine, assign, sendParent, raise } from "xstate-4";
import type { InterpreterFrom } from "xstate-4";
import { useActor } from "@xstate-4/vue";
import { xlog } from "vue-app/utils/xstate";
import axios from "axios";
import { validateEmail } from "vue-app/utils/validators";
import { DTOWhatsappTemplate } from "vue-app/api/dto";

type MessageType = "EMAIL" | "WHATSAPP";

type Context = {
  subject: string;
  body: string;
  email: string;
  phone: string;
  messageType: MessageType;
  whatsappTemplates: DTOWhatsappTemplate[] | null;
  selectedTemplate: { name: string; value: string } | null;
  sessionStorage: Storage | null;
  searchResult: SearchCustomer[];
  searchToken: AbortController | null;
};

export type SearchCustomer = {
  customerId: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
};

type Storage = {
  subject: string;
  body: string;
  email: string;
  phone: string;
  selectedTemplate: { name: string; value: string };
};

export const machineInboxNewMessage = createMachine(
  {
    id: "InboxNewMessage",
    tsTypes: {} as import("./machineInboxNewMessage.typegen").Typegen0,
    schema: {
      events: {} as
        | { type: "CLOSE" }
        | { type: "OPEN" }
        | { type: "SET_EMAIL"; email: string }
        | { type: "SET_PHONE"; phone: string }
        | { type: "SET_SUBJECT"; subject: string }
        | { type: "SET_BODY"; body: string }
        | { type: "SET_TEMPLATE"; template: { name: string; value: string } }
        | { type: "CHANGE_TYPE"; messageType: MessageType; integrated?: boolean }
        | { type: "SEARCH"; query: string; variant?: "EMAIL" | "PHONE" }
        | { type: "ADD_NEW_CUSTOMER"; phone?: string; email?: string; variant: "EMAIL" | "PHONE" }
        | { type: "error.platform.sendingMessage"; data: Error }
        | { type: "CONFIRM" },
      context: {} as Context,
      services: {} as {
        sendingMessage: {
          data: null;
        };
        fetchingWhatsappTemplates: {
          data: DTOWhatsappTemplate[];
        };
        searchingCustomer: {
          data: SearchCustomer[];
        };
      },
    },
    context: {
      subject: "",
      body: "",
      email: "",
      phone: "",
      messageType: "EMAIL",
      whatsappTemplates: null,
      selectedTemplate: null,
      sessionStorage: null,
      searchResult: [],
      searchToken: null,
    },
    initial: "idle",
    entry: ["assignSessionStorage", "assignContextData"],
    states: {
      idle: {
        tags: ["idle"],
        on: {
          CHANGE_TYPE: [
            {
              actions: ["assignType"],
              target: "fetchWhatsappTemplates",
              cond: (context, event) =>
                event.messageType === "WHATSAPP" && !context.whatsappTemplates?.length && event.integrated === true,
            },
            {
              actions: ["assignType"],
            },
          ],
          SET_SUBJECT: {
            actions: ["assignMessageSubject"],
          },
          SET_BODY: {
            actions: ["assignMessageBody"],
          },
          SET_TEMPLATE: {
            actions: ["assignSelectedTemplate"],
          },
          CONFIRM: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "sendMessage",
              cond: "canConfirm",
            },
          ],
          CLOSE: {
            actions: ["saveContextInSessionStorage", sendParent("CLOSE_NEW_MESSAGE_MODAL")],
          },
          SEARCH: {
            target: "searchingCustomer",
          },
          ADD_NEW_CUSTOMER: [
            {
              actions: ["assignSearchToken", "assignMessagePhone", "addNewCustomerToSearchResult"],
              cond: (context, event) => event.variant === "PHONE",
            },
            {
              actions: ["assignSearchToken", "assignMessageEmail", "addNewCustomerToSearchResult"],
              cond: (context, event) => event.variant === "EMAIL",
            },
          ],
          SET_EMAIL: {
            actions: ["assignMessageEmail"],
          },
          SET_PHONE: {
            actions: ["assignMessagePhone"],
          },
        },
      },
      sendMessage: {
        tags: ["sendMessage"],
        invoke: {
          src: "sendingMessage",
          id: "sendingMessage",
          onDone: {
            target: "idle",
            actions: ["clearContextData", raise("CLOSE"), "showSuccessMessage"],
          },
          onError: {
            target: "idle",
            actions: ["showErrorMessageFromSendMessage"],
          },
        },
      },
      fetchWhatsappTemplates: {
        tags: ["fetchWhatsappTemplates"],
        invoke: {
          src: "fetchingWhatsappTemplates",
          id: "fetchingWhatsappTemplates",
          onDone: {
            target: "idle",
            actions: ["assignWhatsappTemplates"],
          },
          onError: {
            target: "idle",
            actions: ["assignWhatsappTemplates", "showErrorMessageForFetchTemplates"],
          },
        },
      },
      searchingCustomer: {
        tags: ["searchingCustomer"],
        entry: ["assignSearchToken"],
        invoke: {
          src: "searchingCustomer",
          id: "searchingCustomer",
          onDone: {
            target: "idle",
            actions: ["assignSearchResults"],
          },
          onError: {
            target: "idle",
            actions: ["assignSearchResults"],
          },
        },
        on: {
          SEARCH: {
            target: "searchingCustomer",
          },
          ADD_NEW_CUSTOMER: [
            {
              actions: ["assignSearchToken", "assignMessagePhone", "addNewCustomerToSearchResult"],
              target: "idle",
              cond: (context, event) => event.variant === "PHONE",
            },
            {
              actions: ["assignSearchToken", "assignMessageEmail", "addNewCustomerToSearchResult"],
              target: "idle",
              cond: (context, event) => event.variant === "EMAIL",
            },
          ],
          SET_EMAIL: {
            actions: ["assignMessageEmail"],
          },
          SET_PHONE: {
            actions: ["assignMessagePhone"],
          },
        },
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignType: assign({
        messageType: (context, event) => {
          xlog({ label: "assignType", type: "action", context, event });
          return event.messageType;
        },
      }),
      assignMessageBody: assign({
        body: (context, event) => {
          xlog({ label: "assignMessageBody", type: "action", context, event });
          return event.body;
        },
      }),
      assignMessageSubject: assign({
        subject: (context, event) => {
          xlog({ label: "assignMessageSubject", type: "action", context, event });
          return event.subject;
        },
      }),
      assignMessageEmail: assign({
        email: (context, event) => {
          xlog({ label: "assignMessageEmail", type: "action", context, event });
          if (!validateEmail(event.email || "")) {
            window.showToast("Please enter a valid email address", "error");
          }
          return event.email || "";
        },
      }),
      assignMessagePhone: assign({
        phone: (context, event) => {
          xlog({ label: "assignMessagePhone", type: "action", context, event });
          if (!validatePhone(event.phone || "")) {
            window.showToast("Please enter a valid phone number", "error");
          }
          return event.phone || "";
        },
      }),
      assignWhatsappTemplates: assign({
        whatsappTemplates: (context, event) => {
          xlog({ label: "assignWhatsappTemplates", type: "action", context, event });
          return (event.data || []) as DTOWhatsappTemplate[];
        },
      }),
      assignSearchResults: assign({
        searchResult: (context, event) => {
          xlog({ label: "assignSearchResults", type: "action", context, event });
          return (event.data || []) as SearchCustomer[];
        },
      }),
      assignSearchToken: assign({
        searchToken: (context, event) => {
          xlog({ label: "assignSearchToken", type: "action", context, event });
          context.searchToken?.abort();
          return new AbortController();
        },
      }),
      addNewCustomerToSearchResult: assign({
        searchResult: (context, event) => {
          xlog({ label: "addNewCustomerToSearchResult", type: "action", context, event });
          return [
            {
              customerId: null,
              email: event.email || "",
              phone: event.phone || "",
              firstName: "",
              lastName: "",
            },
            ...context.searchResult,
          ] as SearchCustomer[];
        },
      }),
      assignSelectedTemplate: assign({
        selectedTemplate: (context, event) => {
          xlog({ label: "assignSelectedTemplate", type: "action", context, event });
          return event.template;
        },
      }),
      saveContextInSessionStorage: (context, event) => {
        xlog({ label: "saveContextInSessionStorage", type: "action", context, event });
        sessionStorage.setItem(
          "newMessageContext",
          JSON.stringify({
            email: context.email,
            phone: context.phone,
            subject: context.subject,
            body: context.body,
            selectedTemplate: context.selectedTemplate,
          })
        );
      },
      assignSessionStorage: assign({
        sessionStorage: (context, event) => {
          xlog({ label: "assignSessionStorage", type: "action", context, event });
          return JSON.parse(sessionStorage.getItem("newMessageContext") || "null");
        },
      }),
      assignContextData: assign({
        email: (context) => context.sessionStorage?.email || "",
        phone: (context) => context.sessionStorage?.phone || "",
        subject: (context) => context.sessionStorage?.subject || "",
        body: (context) => context.sessionStorage?.body || "",
        selectedTemplate: (context) => context.sessionStorage?.selectedTemplate || null,
      }),
      clearContextData: assign({
        email: () => "",
        phone: () => "",
        subject: () => "",
        body: () => "",
        selectedTemplate: () => null,
      }),
      showErrorMessageForFetchTemplates: (context, event) => {
        xlog({ label: "showErrorMessageForFetchTemplates", type: "action", context, event });
        window.showToast("Error while fetching WhatsApp templates", "error");
      },
      showErrorMessageFromSendMessage: (context, event) => {
        xlog({ label: "showErrorMessageFromSendMessage", type: "action", context, event });
        if (event.data.message === "FREEMIUM_LIMIT_REACHED") {
          window.showToast(window._trans._freemium.toasts.error.limitReached, "error");
        } else {
          window.showToast(window._trans._inbox.inboxConversations.notifications.newMessageError, "error");
        }
      },
      showSuccessMessage: (context, event) => {
        xlog({ label: "showSuccessMessage", type: "action", context, event });
        window.showToast(window._trans._inbox.inboxConversations.notifications.newMessageSuccess, "success");
      },
      showIsDemoToast: (context, event) => {
        xlog({ label: "showIsDemoToast", type: "action", context, event });
        window.showToast(window._trans._inbox.inboxDemo.disabled, "info");
      },
    },
    guards: {
      canConfirm: (context, event) => {
        xlog({ label: "canConfirm", type: "guard", context, event });
        switch (context.messageType) {
          case "EMAIL":
            return validateEmail(context.email) && !!context.body && !!context.subject;
          case "WHATSAPP":
            return validatePhone(context.phone) && !!context.selectedTemplate;
        }
      },
      isDemo: () => {
        xlog({ label: "isDemo", type: "guard" });
        return window.EDRONE_CONTEXT.isDemo;
      },
    },
    services: {
      sendingMessage: async (context, event) => {
        xlog({ label: "sendingMessage", context, event });
        const response = await axios.post("/inbox/create-conversation", {
          type_id: context.messageType === "WHATSAPP" ? 8 : 1,
          email: context.email,
          subject: context.subject,
          phone: context.phone,
          content: context.messageType === "WHATSAPP" ? context.selectedTemplate?.value : context.body,
        });
        if (response.data.status !== "SUCCESS") {
          throw new Error(response.data.status);
        }
        return response.data.status;
      },
      fetchingWhatsappTemplates: async (context, event) => {
        xlog({ label: "fetchingWhatsappTemplates", context, event });
        if (event.messageType !== "WHATSAPP") return [];
        const response = await axios.get("/whatsapp-vonage/templates/v2");
        return response.data;
      },
      searchingCustomer: async (context, event) => {
        xlog({ label: "searchingCustomer", context, event });
        if (!event.query && (context.email || context.phone)) {
          return context.searchResult.length
            ? context.searchResult
            : [{ customerId: null, email: context.email, phone: context.phone, firstName: "", lastName: "" }];
        }
        const response = await axios.post(
          `/customer/search?text=${event.query}`,
          {},
          { signal: context.searchToken?.signal }
        );
        return response.data.data;
      },
    },
  }
);

function validatePhone(phone: string) {
  return /^[+]?\d+/g.test(phone);
}

export type InboxNewMessageActor = InterpreterFrom<typeof machineInboxNewMessage>;

// @ts-ignore
export function useInboxNewMessage(service) {
  return useActor(service.state.value.children.createMessage as InboxNewMessageActor);
}
